import _extends from '@babel/runtime/helpers/extends';
import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useCombobox } from 'downshift';
import PropTypes from 'prop-types';
import useModalPortal from './useModalPortal.js';
import { F as FormLabel } from './FormLabel-69bdcdb4.js';
import { G as Group } from './Group-ba54dd0f.js';
import { T as Typography } from './Typography-a7729cdc.js';
import { W as Wrapper } from './Wrapper-8b44541d.js';
import IconCheck from './IconCheck.js';
import { L as Loader } from './Loader-7b33a1cc.js';
import { I as Input } from './Input-9832ee7f.js';

function useBoxPosition({
  anchorRef,
  // anchor element, assume width of dropdown will be same width as this
  position = 'bottom',
  height = 300,
  gap = 4,
  onInputHidden
}) {
  const [boxSize, setBoxSize] = useState({
    width: 0,
    height,
    maxHeight: height
  });
  const [boxPosition, setBoxPosition] = useState({
    x: 0,
    y: 0
  });
  const boxContentSizeRef = useRef({
    width: 0,
    height: 0
  });
  const handleIntersection = entries => {
    const entry = entries[0]; // There's only one observed element (the input)
    if (!entry.isIntersecting) {
      if (onInputHidden && typeof onInputHidden === 'function') {
        onInputHidden();
      }
    }
  };

  // Set up Intersection Observer
  useEffect(() => {
    const options = {
      root: null,
      // Observing the viewport
      rootMargin: '0px',
      // No margin adjustment
      threshold: 0.1 // Trigger when 10% of the input is visible
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    const currentAnchor = anchorRef.current;
    if (currentAnchor) {
      observer.observe(currentAnchor); // Start observing the input element
    }

    // Cleanup observer when the component unmounts or when the dropdown closes
    return () => {
      if (currentAnchor) {
        observer.unobserve(currentAnchor);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run once when component mounts

  const recalculate = useCallback(() => {
    // console.log('recalculate', boxContentSizeRef.current.width);
    if (!anchorRef.current || boxContentSizeRef.current.height === 0) return;
    // console.log('recalculating dropdown position');
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const boxContentRect = boxContentSizeRef.current;
    const x = anchorRect.left + window.scrollX;
    let y = anchorRect.top + window.scrollY;
    let maxBoxHeight = height;
    const availableHeightAbove = anchorRect.top - gap;
    const maxBoxHeightTop = Math.min(availableHeightAbove, height, boxContentRect.height);
    const topY = Math.max(0, anchorRect.top - maxBoxHeightTop - gap);
    const availableHeightBottom = window.innerHeight - (anchorRect.bottom + gap);
    const bottomY = anchorRect.bottom + gap;
    // eslint-disable-next-line max-len
    const maxBoxHeightBottom = Math.min(window.innerHeight - bottomY, height, boxContentRect.height);
    switch (position) {
      case 'top':
        {
          if (availableHeightAbove >= 100) {
            maxBoxHeight = maxBoxHeightTop;
            y = topY;
          } else {
            maxBoxHeight = maxBoxHeightBottom;
            y = bottomY;
          }
          break;
        }
      case 'bottom':
      default:
        if (availableHeightBottom >= 100) {
          maxBoxHeight = maxBoxHeightBottom;
          y = bottomY;
        } else {
          maxBoxHeight = maxBoxHeightTop;
          y = topY;
        }
        break;
    }
    setBoxSize({
      width: anchorRect.width,
      height: maxBoxHeight
    });
    setBoxPosition({
      x,
      y
    });

    // console.log('end recalculate', anchorRect.width);
  }, [anchorRef, gap, height, position]);
  const setBoxContentDimensions = contentRect => {
    // console.log('setting contentdimension', contentRect.height, boxContentSizeRef.current.width);
    boxContentSizeRef.current = {
      ...boxContentSizeRef.current,
      ...contentRect
    };
    recalculate();
  };
  useEffect(() => {
    let frameId;
    const updatePosition = () => {
      // Request the next frame for continuous updates
      frameId = requestAnimationFrame(recalculate);
    };
    const handleScroll = () => {
      // console.log('handleScroll', boxContentSizeRef.current.height);
      cancelAnimationFrame(frameId); // Cancel any pending requestAnimationFrame
      updatePosition(); // Immediately call updatePosition
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updatePosition); // Handle resizing of window

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', recalculate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRef, gap, height, position, recalculate]);
  return {
    boxPosition,
    boxSize,
    recalculate,
    setBoxContentDimensions
  };
}

function ComboboxContent({
  children,
  onSizeChange
}) {
  const [height, setHeight] = useState(0); // Track the element height
  const targetRef = useRef(null);

  // ResizeObserver for element resizing
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        // Only update the height if it's different
        const newHeight = entry.contentRect.height;
        if (newHeight !== height) {
          setHeight(newHeight);
          // console.log('new height from content', entry.contentRect);
          onSizeChange({
            height: newHeight
          });
        }
      });
    });
    if (targetRef.current) {
      resizeObserver.observe(targetRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [height, onSizeChange]); // Only re-run this effect if `height` changes

  return /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "none",
    valign: "end",
    fullWidth: true,
    fullHeight: true,
    componentRef: targetRef
  }, children);
}
ComboboxContent.propTypes = {
  children: PropTypes.any,
  onSizeChange: PropTypes.func.isRequired
};
ComboboxContent.defaultProps = {
  children: null
};

function ComboboxOptions({
  items = [],
  getItemProps,
  highlightedIndex,
  selectedItem,
  status = 'ready',
  emptyContent
}) {
  if (status === 'loading') return /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      all: 'md'
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Group, {
    align: "center",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "md"
  })));
  let options = [];
  const isGrouped = items[0] && items[0].groupLabel;
  const flattenedItems = isGrouped ? items.flatMap(group => group.items) // Flatten nested options
  : items; // Already a flat list, no need to modify

  const generateOption = ({
    item,
    ref,
    isHighlighted,
    isSelected,
    otherProps
  }) => /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    key: `${item.id}-${item.label}`,
    ref: ref,
    className: `${isSelected && 'st_text--color-blurple'} st_width--full st_p-ver-xs st_p-hor-sm st_border--none st_text--md st_text--block st_text--align-left st_hoverable st_clickable ${isHighlighted || isSelected ? 'st_hoverable-hover' : 'st_bg--content'}`
  }, otherProps), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    align: "space-between"
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "ink"
  }, item.label), " ", isSelected && /*#__PURE__*/React.createElement(IconCheck, null)));
  if (items[0] && isGrouped) {
    // Loop through groups with a group label
    options = items.map(group => /*#__PURE__*/React.createElement(React.Fragment, {
      key: `${group.groupLabel}-${group?.id}`
    }, /*#__PURE__*/React.createElement(Wrapper, {
      padding: {
        horizontal: 'sm',
        bottom: 'sm',
        top: 'md'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      size: "sm",
      color: "asphalt"
    }, group.groupLabel)), group.items.map(item => {
      // eslint-disable-next-line max-len
      const flattenedIndex = flattenedItems.findIndex(flattenedItem => flattenedItem.id === item.id);
      const {
        ref,
        ...otherProps
      } = getItemProps({
        item,
        index: flattenedIndex
      });
      return generateOption({
        item,
        ref,
        isHighlighted: highlightedIndex === flattenedIndex || selectedItem?.id === item.id,
        isSelected: selectedItem?.id === item.id,
        otherProps
      });
    })));
  } else if (items[0]) {
    // Loop through flat list of items
    options = items.map((item, index) => {
      const {
        ref,
        ...otherProps
      } = getItemProps({
        item,
        index
      });
      return generateOption({
        item,
        ref,
        isHighlighted: highlightedIndex === index || selectedItem?.id === item.id,
        isSelected: selectedItem?.id === item.id,
        otherProps
      });
    });
  } else {
    // No items found
    options = /*#__PURE__*/React.createElement(Wrapper, {
      padding: {
        all: 'md'
      },
      fullWidth: true
    }, emptyContent || /*#__PURE__*/React.createElement(Typography, {
      variant: "bodytext"
    }, "No results found"));
  }
  return options;
}
ComboboxOptions.propTypes = {
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    groupLabel: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    }))
  }), PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })])),
  getItemProps: PropTypes.func.isRequired,
  highlightedIndex: PropTypes.number,
  selectedItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }),
  status: PropTypes.oneOf(['ready', 'loading']),
  emptyContent: PropTypes.node
};
ComboboxOptions.defaultProps = {
  items: [],
  highlightedIndex: null,
  selectedItem: null,
  status: 'ready',
  emptyContent: null
};

const Combobox = /*#__PURE__*/React.forwardRef(({
  position,
  height,
  icon,
  items,
  label,
  placeholder,
  disabled,
  initialSelectedItem,
  emptyContent,
  status,
  onSelectedItemChange,
  selectedItem,
  showReset,
  onInputValueChange,
  // eslint-disable-next-line max-len
  filterFunction = (needle, haystack = []) => haystack.filter(item => item.label.toLowerCase().includes(needle.toLowerCase())),
  otherComboboxProps,
  revertOnBlur
}, ref) => {
  const [itemsForOptions, setItemsForOptions] = useState(items);
  const scrollableAncestors = useRef([]); // Track scrollable ancestors

  const convertItemsForHook = nestedItems => {
    // console.log('convert nested items for hook', nestedItems);
    const isGrouped = nestedItems[0] && nestedItems[0].groupLabel;
    return isGrouped ? nestedItems.flatMap(group => group.items) : nestedItems;
  };
  const filterFunctionNested = useCallback((needle, haystack = []) => haystack.map(group => {
    // console.log('filtering nested', needle, group);
    const filteredItems = filterFunction(needle, group.items);
    if (filteredItems.length > 0) {
      return {
        ...group,
        items: filteredItems
      };
    }
    return null;
  }).filter(group => group !== null), [filterFunction]);
  const filterForOptions = useCallback((needle, haystack = []) => {
    const isGrouped = haystack[0] && haystack[0].groupLabel;
    return isGrouped ? filterFunctionNested(needle, haystack) : filterFunction(needle, haystack);
  }, [filterFunction, filterFunctionNested]);
  const [itemsForHook, setItemsForHook] = useState(convertItemsForHook(items));
  const inputWidthRef = useRef(null);
  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem: internalSelectedItem,
    reset,
    openMenu,
    closeMenu,
    setInputValue: setInternalInputValue,
    inputValue
  } = useCombobox({
    items: itemsForHook,
    onInputValueChange: ({
      inputValue: newInputValue
    }) => {
      // console.log('onInputValueChange', newInputValue);
      setItemsForHook(filterFunction(newInputValue, convertItemsForHook(items)));
      setItemsForOptions(filterForOptions(newInputValue, items));
      if (typeof onInputValueChange === 'function') onInputValueChange(newInputValue);
    },
    itemToString(item) {
      return item ? item.label : '';
    },
    selectedItem,
    // eslint-disable-next-line no-shadow
    onSelectedItemChange: ({
      selectedItem
    }) => {
      if (typeof onSelectedItemChange === 'function') onSelectedItemChange(selectedItem);
    },
    initialSelectedItem,
    isDisabled: disabled,
    ...otherComboboxProps
  });
  useImperativeHandle(ref, () => ({
    reset,
    openMenu,
    closeMenu
  }));

  // if user blurs out without selecting anything, but has changed the input,
  // then reset the input value to the last selected item
  const handleInputBlur = () => {
    const resetInput = () => {
      setInternalInputValue(internalSelectedItem?.label ?? '');
    };
    if (revertOnBlur && inputValue !== internalSelectedItem?.label) {
      // there is a sporadic race condition and this is a workaround
      setTimeout(resetInput, 200);
    }
  };
  const {
    renderNode
  } = useModalPortal();
  const dropdownRef = useRef(null);
  const menuProps = getMenuProps();
  const {
    boxPosition,
    boxSize,
    setBoxContentDimensions,
    recalculate
  } = useBoxPosition({
    height,
    boxRef: dropdownRef,
    anchorRef: inputWidthRef,
    position,
    boxContentRef: dropdownRef,
    onInputHidden: closeMenu
  });
  const {
    ref: downshiftInputRef,
    ...otherDownshiftInputProps
  } = getInputProps({
    disabled,
    placeholder,
    onBlur: handleInputBlur
  });
  useEffect(() => {
    setItemsForOptions(items);
    setItemsForHook(convertItemsForHook(items));
  }, [items]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    let frameId;
    const isScrollable = element => {
      const style = window.getComputedStyle(element);
      return style.overflow === 'auto' || style.overflow === 'scroll';
    };
    const updatePosition = () => {
      frameId = requestAnimationFrame(recalculate);
    };
    const handleAncestorScroll = () => {
      cancelAnimationFrame(frameId);
      updatePosition();
    };
    const findScrollableAncestors = () => {
      const dropdownElement = inputWidthRef.current;
      let parent = dropdownElement?.parentElement;
      const scrollableContainers = [];
      while (parent && parent !== document.body) {
        if (isScrollable(parent)) {
          scrollableContainers.push(parent);
        }
        parent = parent.parentElement;
      }
      scrollableAncestors.current = scrollableContainers;
    };
    if (isOpen) {
      findScrollableAncestors(); // Find scrollable ancestors when dropdown opens

      // Attach scroll listeners to all scrollable ancestors
      scrollableAncestors.current.forEach(ancestor => {
        ancestor.addEventListener('scroll', handleAncestorScroll);
      });

      // Cleanup event listeners on unmount or when dropdown closes
      return () => {
        scrollableAncestors.current.forEach(ancestor => {
          ancestor.removeEventListener('scroll', handleAncestorScroll);
        });
      };
    }
  }, [isOpen, recalculate]); // Re-run when dropdown opens or scrollable ancestors change

  const content = /*#__PURE__*/React.createElement("div", _extends({
    style: {
      position: 'fixed',
      top: boxPosition.y,
      left: boxPosition.x,
      width: boxSize.width,
      maxHeight: `${boxSize.height}px`,
      borderRadius: 'var(--st-border-radius)'
    },
    className: `st_overflow--auto st_boxsize--content st_bg--content ${isOpen && 'st_border--t st_border--b st_border--l st_border--r'}`
  }, menuProps), isOpen && /*#__PURE__*/React.createElement(ComboboxContent, {
    onSizeChange: setBoxContentDimensions
  }, /*#__PURE__*/React.createElement(ComboboxOptions, {
    items: itemsForOptions,
    emptyContent: emptyContent,
    getItemProps: getItemProps,
    highlightedIndex: highlightedIndex,
    selectedItem: selectedItem ?? internalSelectedItem,
    status: status
  })));
  const showResetButton = showReset && !!(selectedItem || inputValue);
  return /*#__PURE__*/React.createElement(React.Fragment, null, label ? /*#__PURE__*/React.createElement(FormLabel, _extends({
    label: label,
    labelPosition: "top"
  }, getLabelProps(), {
    fullWidth: true
  }), /*#__PURE__*/React.createElement("div", {
    ref: inputWidthRef,
    className: "st_width--full st_boxsize--content st_m-none st_p-none"
  }, /*#__PURE__*/React.createElement(Input, _extends({
    icon: icon,
    onReset: showResetButton && reset ? reset : null,
    componentRef: downshiftInputRef
  }, otherDownshiftInputProps)))) : /*#__PURE__*/React.createElement("div", {
    ref: inputWidthRef,
    className: "st_width--full st_boxsize--content st_m-none st_p-none"
  }, /*#__PURE__*/React.createElement(Input, _extends({
    icon: icon,
    onReset: showResetButton && reset ? reset : null,
    componentRef: downshiftInputRef
  }, otherDownshiftInputProps))), renderNode ? /*#__PURE__*/createPortal(content, renderNode) : content);
});
Combobox.propTypes = {
  /** Position of dropdown relative to text input, will be overwritten if there is no space */
  position: PropTypes.oneOf(['top', 'bottom']),
  /** Height of dropdown, will be overwritten if there is no space */
  height: PropTypes.number,
  /** optional icon to be displayed to the left. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any,
  /** list of items, either a flat array of objects for a simple list of items
   * or an array of objects with a `groupLabel` and `items` for a grouped list of items
   */
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }), PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    groupLabel: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    }))
  })])),
  /** text label for form input */
  label: PropTypes.string,
  /** placeholder if form input is empty */
  placeholder: PropTypes.string,
  /** whether the form input is disabled */
  disabled: PropTypes.bool,
  /** initial selected item */
  initialSelectedItem: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }),
  /** jsx to override default empty message when there are no matches */
  emptyContent: PropTypes.any,
  /** current status of dropdown contents, used for showing loading state if needed */
  status: PropTypes.oneOf(['ready', 'loading']),
  /** callback for when selected item is changed, via the keyboard or mouse click */
  onSelectedItemChange: PropTypes.func,
  /** the current selected item. Turns the component into a controlled component. */
  selectedItem: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }),
  /** callback for when a user types into the text input */
  onInputValueChange: PropTypes.func,
  /** function used to filter content */
  filterFunction: PropTypes.func,
  /** other props to pass down to useCombobox() hook used internally by component */
  otherComboboxProps: PropTypes.shape({}),
  /** whether to show reset button */
  showReset: PropTypes.bool,
  /** whether to revert value of input on blur if nothing was selected */
  revertOnBlur: PropTypes.bool
};
Combobox.defaultProps = {
  position: 'bottom',
  height: 300,
  items: [],
  label: '',
  placeholder: '',
  disabled: false,
  status: 'ready',
  // eslint-disable-next-line max-len
  filterFunction: (needle, haystack = []) => haystack.filter(item => item.label.toLowerCase().includes(needle.toLowerCase())),
  icon: null,
  initialSelectedItem: null,
  emptyContent: null,
  onSelectedItemChange: null,
  selectedItem: undefined,
  onInputValueChange: null,
  otherComboboxProps: {},
  showReset: true,
  revertOnBlur: true
};
Combobox.displayName = 'Combobox';
Combobox.Content = ComboboxContent;
Combobox.Options = ComboboxOptions;
var Combobox$1 = Combobox;

export { Combobox$1 as C };
